import styles from "./table.module.css";

import { useState, useEffect } from "react";

import { Alert, Button, Drawer, Popover, Row, Tag } from "@narmi/design_system";
import { Payment } from "./PaymentTable";
import LabeledContentRow from "./LabeledContentRow";
import { moneyFormatter } from "./PaymentTable";
import InformationRequestDialog from "./InformationRequestDialog";
import { useNavigate } from "react-router-dom";
import {
  PAYMENT_EVENT_TYPE_MAP,
  PAYMENT_STATUSES,
  PAYMENT_DIRECTIONS,
  PAYMENT_EVENT_TYPE_MAP_TYPE,
  TERMINAL_STATES,
  ACWP_REJECT_REASONS,
  ACWP_REJECT_REASONS_TYPE,
} from "./codesets";
import RelatedPayments from "./RelatedPayments";
import RelatedEvaluations from "./RelatedEvaluations";
import RelatedCases from "./RelatedCases";
import { Case } from "./CaseTable";
import { HistoryRow } from "./CaseHistoryList";
import PostingFailedDialog from "./PostingFailedDialog";
import ReturnRequestDialog from "./ReturnRequestDialog";
import CopyableCell from "../CopyableCell";
import { useUserConfiguration } from "@hooks/useUserConfiguration";
import { AdminApiSchema } from "../../api_utils";
import { AlloyEvaluationModal } from "@components/alerts/alloy_evaluations/AlloyEvaluationModal";
import { AlloyEvaluationType } from "@components/alerts/alloy_evaluations/AlloyEvaluationSummary";
import { evaluationForMeta } from "./FedNowTransactionDrawer";

type PaymentEventMetadata = {
  author: string | undefined;
  reason: ACWP_REJECT_REASONS_TYPE | undefined;
  narrative: string | undefined;
  memo: string | undefined;
  exists: boolean | undefined;
  valid: boolean | undefined;
  customer_online: boolean | undefined;
  names: Array<string> | undefined;
};
type FedNowTransaction = AdminApiSchema.components["schemas"]["FedNowTransactionAdmin"];

export type PaymentEvent = {
  created_at: string;
  type: PAYMENT_EVENT_TYPE_MAP_TYPE;
  metadata: PaymentEventMetadata;
};

export const dateOptions: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "numeric",
  hour12: true,
};

export async function fetchPaymentHistory(setHistoryItems: CallableFunction, payment: Payment) {
  const lookupRes = await fetch(`/v1/fednow/payments/${payment?.payment_identification}/history`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!lookupRes.ok) {
    return;
  }
  const result = await lookupRes.json();
  setHistoryItems(result);
}

function describeAdditionalInfo(event: PaymentEvent) {
  if (event.type === "rejected" && event.metadata?.reason) {
    if (ACWP_REJECT_REASONS[event.metadata.reason]) {
      return `${ACWP_REJECT_REASONS[event.metadata.reason]} (${event.metadata.reason})`;
    } else {
      return `Reject reason code: ${event.metadata.reason}`;
    }
  }
  if (event.type === "rejected" && event.metadata && Object.keys(event.metadata).length > 0) {
    if (!event.metadata.customer_online) {
      return "Reject reason: customer offline";
    } else if (!event.metadata.exists) {
      return "Reject reason: account does not exist";
    } else if (!event.metadata.valid) {
      return "Reject reason: account is not a valid transfer destination";
    }
  }
  return null;
}

export function PaymentDetails({
  historyItems,
  payment,
  relatedTransaction,
}: {
  historyItems: Array<PaymentEvent>;
  payment: Payment;
  relatedTransaction?: FedNowTransaction;
}) {
  const inTerminalState = TERMINAL_STATES.includes(payment.state as "posted");
  const tzDate = new Date(payment.fed_settlement_date);
  const [evalModalOpen, setEvalModalOpen] = useState(false);

  let evaluation: AlloyEvaluationType | null = null;
  if (relatedTransaction) {
    if ((relatedTransaction.metadata as any)?.alloy_ongoing_evaluation_response) {
      evaluation = evaluationForMeta(relatedTransaction.metadata as Record<string, any>);
    } else {
      evaluation = relatedTransaction.alloy_evaluations?.[0];
    }
  }

  // strip out timezone from date so it displays correctly instead of trying to adjust a datetime to the users timezone
  // https://stackoverflow.com/a/39209842/13555818
  const formattedSettlementDate = new Date(
    tzDate.getTime() + tzDate.getTimezoneOffset() * 60000
  ).toLocaleDateString();
  return (
    <div>
      <div className={`${styles.transactionDetailContainer} margin--y padding--all`}>
        <h4 className="fontFamily--body padding--bottom">Payment details</h4>
        <LabeledContentRow label="Payment ID" content={payment.payment_identification} />
        <LabeledContentRow
          label="Core transaction ID"
          content={payment.core_transaction_id || "-"}
        />
        <LabeledContentRow
          label="Status"
          content={PAYMENT_STATUSES[payment.state]}
          contentStyle={{ textTransform: "capitalize" }}
        />
        <LabeledContentRow
          label="Amount"
          content={moneyFormatter.format(payment.amount_cents / 100)}
        />
        <LabeledContentRow label="Direction" content={PAYMENT_DIRECTIONS[payment.type]} />
        <LabeledContentRow label="Memo" content={payment.memo || "-"} />
        <LabeledContentRow label="Effective settlement date" content={formattedSettlementDate} />
      </div>
      <div className={`${styles.sideBySideContainer}`}>
        <div className={`${styles.sideBySideChild} padding--all margin--right--l`}>
          <h4 className="fontFamily--body padding--bottom">Source</h4>
          <LabeledContentRow
            label="Sender"
            content={payment.source_name || "-"}
            contentStyle={{ textTransform: "capitalize" }}
            size="s"
          />
          <LabeledContentRow
            label="Account number"
            content={payment.source_account || "-"}
            size="s"
          />
          <LabeledContentRow
            label="Routing number"
            content={`${payment.source_fi} (${payment.source_fi_name})`}
            size="s"
          />
        </div>
        <div className={`${styles.sideBySideChild} padding--all`}>
          <h4 className="fontFamily--body padding--bottom">Destination</h4>
          <LabeledContentRow
            label="Recipient"
            content={payment.destination_name || "-"}
            contentStyle={{ textTransform: "capitalize" }}
            size="s"
          />
          <LabeledContentRow
            label="Account number"
            content={payment.destination_account}
            size="s"
          />
          <LabeledContentRow
            label="Routing number"
            content={`${payment.destination_fi} (${payment.destination_fi_name})`}
            size="s"
          />
        </div>
      </div>
      <div className={`${styles.transactionDetailContainer} margin--y padding--all`}>
        <h4 className="fontFamily--body padding--bottom">Payment history</h4>
        {historyItems.map((h: PaymentEvent, idx: number) => {
          let additional = describeAdditionalInfo(h);
          let overrideProps = {};
          const rowKind =
            idx === historyItems.length - 1
              ? "start"
              : idx === 0 && !inTerminalState
                ? "pending"
                : "node";
          if (relatedTransaction && evaluation && rowKind === "start" && h.type === "sent") {
            overrideProps = { onClickOverride: () => setEvalModalOpen(true) };
            additional = "View details";
          }
          return (
            <HistoryRow
              content={PAYMENT_EVENT_TYPE_MAP[h.type]}
              timestamp={h.created_at}
              rowType={additional === null ? "no_additional" : "event"}
              kind={rowKind}
              additional={additional}
              author={h.metadata?.author}
              {...overrideProps}
            />
          );
        })}
      </div>
      {evaluation && (
        <AlloyEvaluationModal
          evaluation={evaluation}
          isModalOpen={evalModalOpen}
          setIsModalOpen={setEvalModalOpen}
        />
      )}
    </div>
  );
}

export default function PaymentDetailDrawer({
  isOpen,
  setIsOpen,
  payment,
  refresh,
}: {
  isOpen: boolean;
  setIsOpen: CallableFunction;
  payment: Payment | null;
  refresh: CallableFunction;
}) {
  const [historyItems, setHistoryItems]: [Array<PaymentEvent>, CallableFunction] = useState([]);
  const [relatedCase, setRelatedCase]: [Case | null, CallableFunction] = useState(null);
  const navigate = useNavigate();
  const { isFeatureEnabled } = useUserConfiguration();

  const [infoReqAction, setInfoReqAction]: [boolean, CallableFunction] = useState(false);
  const [morePopoverOpen, setMorePopoverOpen] = useState(false);
  const [returnAction, setReturnAction]: [string | null, CallableFunction] = useState(null);
  const [relatedTransaction, setRelatedTransaction] = useState<FedNowTransaction>();
  const [postingFailedAction, setPostingFailedAction]: [string | null, CallableFunction] =
    useState(null);
  const sendOnly = isFeatureEnabled("fednow_send") && !isFeatureEnabled("fednow_receive");

  useEffect(() => {
    if (payment) {
      fetchPaymentHistory(setHistoryItems, payment);
      getRelatedEvaluations();
    }
  }, [setHistoryItems, payment]);

  if (!payment) {
    return null;
  }

  function returnRequestExists(historyItems: PaymentEvent[]): boolean {
    return historyItems.length > 0 && historyItems.some((item) => item.type === "return_requested");
  }

  function morePopoverOptions() {
    const buttons = [];
    if (sendOnly) {
      return [];
    }
    if (payment) {
      buttons.push(
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setInfoReqAction(true);
          }}
          key="info"
        >
          Request information
        </span>
      );
    }
    if (
      payment &&
      payment.state === "posted" &&
      payment.type === "send" &&
      !returnRequestExists(historyItems)
    ) {
      buttons.push(
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setReturnAction("create");
          }}
          key="return_request"
        >
          Request a return
        </span>
      );
    }

    if (payment && (payment.state === "posting_failed" || payment.state === "final_post_failed")) {
      buttons.push(
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setPostingFailedAction("return");
          }}
          key="return"
        >
          Return payment
        </span>
      );
      buttons.push(
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setPostingFailedAction("manually_resolve");
          }}
          key="manual_resolve"
        >
          Manually resolve
        </span>
      );
    }
    return buttons;
  }

  let alertText = "";
  if (relatedCase) {
    switch ((relatedCase as Case).kind) {
      case "return":
        alertText = "Return requested.";
        break;
      case "ACWP":
        alertText = "Accepted without post.";
        break;
      case "information_request":
        alertText = "Information requested.";
        break;
    }
  }

  async function getRelatedEvaluations() {
    if (!payment) {
      return;
    }
    const res = await fetch(
      `/v2/admin/transfers/instant/?payment_identification=${payment.payment_identification}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!res.ok) {
      return;
    }
    const data = await res.json();
    setRelatedTransaction(data.results[0]);
  }

  const menuOptions = morePopoverOptions();

  return (
    <Drawer depth="60%" isOpen={isOpen} showControls={false} onUserDismiss={() => setIsOpen(false)}>
      <Row alignItems="center">
        <Row.Item>
          <h2 className="whiteSpace--truncate" style={{ maxWidth: "100%" }}>
            {payment.payment_identification}
          </h2>
        </Row.Item>
        <Row.Item shrink>
          <div style={{ textTransform: "capitalize" }}>
            <Tag label={PAYMENT_STATUSES[payment.state]}></Tag>
          </div>
        </Row.Item>
        <Row.Item shrink>
          <div
            style={{
              float: "right",
              borderRight: menuOptions.length > 0 ? "2px solid lightGrey" : "",
            }}
            className={menuOptions.length > 0 ? "padding--right--s" : ""}
          >
            <CopyableCell
              copyText={`${window.location.origin}/fednow/payments/${payment.payment_identification}`}
              text="Copy link"
              style={{ color: "var(--theme-primary)" }}
              buttonSize="s"
            />
          </div>
        </Row.Item>
        <Row.Item shrink>
          <div style={{ float: "right" }}>
            {menuOptions.length > 0 && (
              <Popover
                isOpen={morePopoverOpen}
                content={<div style={{ display: "table" }}>{menuOptions}</div>}
              >
                <Button
                  kind="plain"
                  startIcon="more-vertical"
                  onClick={() => setMorePopoverOpen(!morePopoverOpen)}
                />
              </Popover>
            )}
          </div>
        </Row.Item>
      </Row>
      {relatedCase && (
        <div className="padding--top">
          <Alert
            isDismissable={false}
            isActive={(relatedCase as Case).status === "open"}
            kind="warn"
            icon="alert-triangle"
          >
            <span>{alertText}</span>
            <span style={{ float: "right" }}>
              Sent on{" "}
              {new Date((relatedCase as Case).created_at).toLocaleString("en-US", dateOptions)}
            </span>
            <div className="padding--top--s">
              <a
                href={`/fednow/cases/${(relatedCase as Case).case_identification}`}
                target="_blank"
              >
                View request
              </a>
            </div>
          </Alert>
        </div>
      )}
      <PaymentDetails
        historyItems={historyItems}
        payment={payment}
        relatedTransaction={relatedTransaction}
      />
      <RelatedPayments original={payment} />
      <RelatedEvaluations relatedTransaction={relatedTransaction} />
      <RelatedCases payment={payment} setRelatedCase={setRelatedCase} />
      <InformationRequestDialog
        caseobj={null}
        payment={payment}
        shown={infoReqAction}
        setShown={setInfoReqAction}
        successCallback={() => {
          navigate("/fednow/cases/");
        }}
      />
      <PostingFailedDialog
        payment={payment}
        action={postingFailedAction}
        setAction={setPostingFailedAction}
        successCallback={() => {
          setPostingFailedAction(null);
          setIsOpen(false);
          refresh();
        }}
      />
      <ReturnRequestDialog
        caseobj={null}
        payment={payment}
        action={returnAction}
        setAction={setReturnAction}
        successCallback={(caseId: string) => {
          if (caseId) {
            navigate(`/fednow/cases/${caseId}`);
          } else {
            setPostingFailedAction(null);
            setIsOpen(false);
            refresh();
          }
        }}
      />
    </Drawer>
  );
}
